import { useMemo } from "react"
import { Chart } from "react-chartjs-2"
import { COLOR, EmptyState, ErrorState, formatVnd } from "procurement/design"
import { useGetBudgetDashboardByCategoryQuery } from "shared/redux/endpoints/procurementV2"
import { SERIES, baseOptions, gridColor, pctOf, pctTick, tickColor } from "./chartSetup"

// Spend is a few % of budget and categories differ by orders of magnitude, so
// absolute amounts flatten every bar — each row is normalised to its own budget.
const SEGMENTS = [
  { key: "paid", label: "Paid", color: SERIES.paid },
  { key: "unpaid", label: "Unpaid", color: SERIES.unpaid },
  { key: "committed", label: "Committed", color: SERIES.committed },
]

const shareOf = (r, key) => (Number(r.totalBudget) > 0 ? ((Number(r[key]) || 0) / Number(r.totalBudget)) * 100 : 0)

const CategoryBudgetChart = ({ params }) => {
  const { data, isFetching, isError } = useGetBudgetDashboardByCategoryQuery({ ...params, size: 200 })
  const rows = data?.items || []

  const chart = useMemo(
    () => ({
      labels: rows.map((r) => r.categoryName),
      datasets: [
        ...SEGMENTS.map((s) => ({
          label: s.label,
          moneyKey: s.key,
          data: rows.map((r) => shareOf(r, s.key)),
          backgroundColor: s.color,
          stack: "budget",
          barThickness: 16,
        })),
        {
          label: "Remaining",
          moneyKey: "remaining",
          data: rows.map((r) => Math.max(0, 100 - SEGMENTS.reduce((sum, s) => sum + shareOf(r, s.key), 0))),
          backgroundColor: COLOR.neutral50,
          stack: "budget",
          barThickness: 16,
        },
      ],
    }),
    [rows]
  )

  const maxPct = Math.max(100, ...rows.map((r) => Number(r.usedPercentage) || 0))

  const options = useMemo(
    () => ({
      ...baseOptions,
      indexAxis: "y",
      interaction: { mode: "index", axis: "y", intersect: false },
      scales: {
        x: {
          stacked: true,
          min: 0,
          max: Math.ceil(maxPct / 10) * 10,
          ticks: { callback: pctTick, color: tickColor, stepSize: 10 },
          grid: {
            color: (ctx) => (ctx.tick?.value === 100 ? COLOR.textPrimary : gridColor),
            lineWidth: (ctx) => (ctx.tick?.value === 100 ? 2 : 1),
          },
        },
        y: {
          stacked: true,
          grid: { display: false },
          ticks: { color: (ctx) => (rows[ctx.index]?.isOverBudget ? SERIES.danger : tickColor) },
        },
      },
      plugins: {
        ...baseOptions.plugins,
        tooltip: {
          callbacks: {
            title: (items) => {
              const r = rows[items[0]?.dataIndex]
              return r ? `${r.categoryName} · budget ${formatVnd(r.totalBudget)}` : ""
            },
            label: (ctx) => {
              const r = rows[ctx.dataIndex]
              return `${ctx.dataset.label}: ${formatVnd(r?.[ctx.dataset.moneyKey])} (${pctOf(ctx.raw)})`
            },
            footer: (items) => {
              const r = rows[items[0]?.dataIndex]
              return r ? `Used ${pctOf(r.usedPercentage)}${r.isOverBudget ? " · Over budget" : ""}` : ""
            },
          },
        },
      },
    }),
    [rows, maxPct]
  )

  if (isError) return <ErrorState text="Couldn't load budget by category." />
  if (!isFetching && rows.length === 0) return <EmptyState title="No category budget for this period." />

  return (
    <div className="br-chart" style={{ height: Math.max(240, rows.length * 28 + 80) }}>
      <Chart type="bar" data={chart} options={options} />
    </div>
  )
}

export default CategoryBudgetChart
